import { Card, CardTitle, CardHeader, CardContent } from "@/components/ui/card";
import { ChartConfig, ChartTooltip, ChartTooltipContent, ChartContainer, ChartLegendContent, ChartLegend } from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis, LabelList, ReferenceLine } from "recharts";
import { TextInfoGroupAlt } from "@/components/shared";
import { CostDetails, ShiftType, Tender } from "@/graphql/__generated__/graphql";
import { convertBpToPercentageDecimal, convertPercentageDecimalToDecimal } from "@/utils/helpers";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedNumberInputField } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { JSX, useEffect, useState } from "react";
import { positiveInteger, postiveNumberZeroToOneHundred } from "@/utils/form";
import { useTranslation, Trans } from "react-i18next";
import Decimal from "decimal.js";
import { TFunction } from "i18next";
import { cn } from "@/lib/utils";

/**
 * Represents margin data for a specific year - for chart
 */
type MarginData = {
  year: string;
  tenderMargin: number;
  comparisonMargin: number;
};

/**
 * Props for the MarginErosionText component
 */
interface MarginErosionTextProps {
  type: "tender" | "comparison";
  contractDuration: number;
  marginErosionData: MarginData[];
};

/**
 * Component that displays text explaining margin erosion over time
 * @param props - The component props
 * @returns {JSX.Element | undefined} A paragraph element with translated margin erosion summary
 */
const MarginErosionText = ({ type, contractDuration, marginErosionData }: MarginErosionTextProps): JSX.Element | undefined => {
  if (marginErosionData.length === 0) return;

  const marginKey = type === "tender" ? "tenderMargin" : "comparisonMargin";
  const initialMargin = marginErosionData[0][marginKey];
  const finalMargin = marginErosionData[contractDuration][marginKey];
  const erosionPercentage = (100 - ((finalMargin / initialMargin) * 100)).toFixed(2);
  const highErosion = Number(erosionPercentage) > 25;
  const negativeErosion = Number(erosionPercentage) < 0;
  return (
    <p className={cn("text-sm", highErosion && "text-red-500", negativeErosion && "text-green-500")}>
      <Trans
        i18nKey={
          highErosion
            ? "components:marginerosion.higherosionsummary"
            : negativeErosion
              ? "components:marginerosion.negativeerosionsummary"
              : "components:marginerosion.lowerosionsummary"
        }
        values={{
          contractDuration,
          erosionPercentage: negativeErosion ? Math.abs(Number(erosionPercentage)).toFixed(2) : erosionPercentage,
          finalMargin: finalMargin.toFixed(2),
        }}
        components={{ b: <b /> }}
      />
    </p>
  );
};

/**
 * Schema for the comparison form
 */
const CreateComparisonSchema = (t: TFunction) => z.object({
  operativeMargin: postiveNumberZeroToOneHundred(t("marginerosion.compare.inputs.operativemargin.label")),
  escalationClause: postiveNumberZeroToOneHundred(t("marginerosion.compare.inputs.escalationclause.label")),
  yearlyTariffIncrease: postiveNumberZeroToOneHundred(t("marginerosion.compare.inputs.yearlytariffincrease.label")),
  contractDuration: positiveInteger(t("marginerosion.compare.inputs.contractduration.label")),
});

/**
 * Props for the MarginErosionVisualiserComponent
 */
type MarginErosionVisualiserComponentProps = {
  tenderData: Tender;
  costDetails: CostDetails[];
};

/**
 * Component that displays margin erosion visualisation
 * Also has a comparison form to compare the margin erosion with different values
 * @param props - The component props
 * @returns A card element with margin erosion visualisation
 */
function MarginErosionVisualiserComponent({ tenderData, costDetails }: MarginErosionVisualiserComponentProps) {
  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const [marginErosionData, setMarginErosionData] = useState<MarginData[]>([]);
  const [comparisonData, setComparisonData] = useState<MarginData[] | null>(null);
  const [currentDuration, setCurrentDuration] = useState<number>(0);

  if (tenderData.minutes === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{t("marginerosion.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400">{t("marginerosion.nodata")}</p>
        </CardContent>
      </Card>
    );
  }

  // Constants
  const tenderHours = (tenderData.minutes ?? 0) / 60;
  const escalationClause = convertBpToPercentageDecimal(tenderData.contract?.escalationClause ?? 0);
  const operativeMargin = convertBpToPercentageDecimal(tenderData.margin ?? 0);
  const contractDurationMonths = tenderData.contract?.durationMonths;
  const contractDuration = Math.ceil(contractDurationMonths / 12);
  const yearlyTariffIncrease = 0.05;
  const tenderCostDetails = costDetails.find((cost) => cost.type === ShiftType.Cumulated);

  // Calculations
  const wage = new Decimal(tenderCostDetails?.payCosts.totalPayCosts ?? 0)
    .minus(tenderCostDetails?.payCosts.nonTariffAllowances ?? 0)
    .dividedBy(tenderHours)
    .toNumber();

  const price = new Decimal(tenderCostDetails?.totalCosts ?? 0)
    .dividedBy(tenderHours)
    .dividedBy(new Decimal(1).minus(convertBpToPercentageDecimal(tenderData.margin ?? 0)))
    .toNumber();

  const otherCosts = new Decimal(tenderCostDetails?.payCosts.nonTariffAllowances ?? 0)
    .plus(tenderCostDetails?.materialCosts.totalMaterialCosts ?? 0)
    .dividedBy(tenderHours)
    .toNumber();

  const chartConfig = {
    tenderMargin: {
      label: t("marginerosion.graph.tendermargin"),
      color: "#3b82f6",
    },
    comparisonMargin: {
      label: t("marginerosion.graph.comparisonmargin"),
      color: "#f97316",
    },
  } satisfies ChartConfig;

  /**
   * Defines the input fields for the comparison form
   */
  const comparisonInputFields = [
    {
      label: t("marginerosion.compare.inputs.operativemargin.label"),
      name: "operativeMargin",
      placeholder: t("marginerosion.compare.inputs.operativemargin.placeholder"),
      style: "decimal",
      additionalSuffix: "%",
    },
    {
      label: t("marginerosion.compare.inputs.escalationclause.label"),
      name: "escalationClause",
      placeholder: t("marginerosion.compare.inputs.escalationclause.placeholder"),
      style: "decimal",
      additionalSuffix: "%",
    },
    {
      label: t("marginerosion.compare.inputs.yearlytariffincrease.label"),
      name: "yearlyTariffIncrease",
      placeholder: t("marginerosion.compare.inputs.yearlytariffincrease.placeholder"),
      style: "decimal",
      additionalSuffix: "%",
    },
    {
      label: t("marginerosion.compare.inputs.contractduration.label"),
      name: "contractDuration",
      placeholder: t("marginerosion.compare.inputs.contractduration.placeholder"),
      style: "decimal",
      additionalSuffix: t("marginerosion.compare.inputs.contractduration.suffix"),
      isInteger: true
    },
  ];

  const ComparisonSchema = CreateComparisonSchema(t);

  /**
   * Initializes the form for comparing margin erosion
   */
  const form = useForm<z.infer<typeof ComparisonSchema>>({
    resolver: zodResolver(ComparisonSchema) as Resolver<z.infer<typeof ComparisonSchema>>,
    defaultValues: {
      operativeMargin: convertPercentageDecimalToDecimal(operativeMargin),
      escalationClause: convertPercentageDecimalToDecimal(escalationClause),
      yearlyTariffIncrease: convertPercentageDecimalToDecimal(yearlyTariffIncrease),
      contractDuration,
    },
    mode: "onChange",
  });

  /**
   * Calculates margin erosion over time based on given parameters
   * @param price - The initial price
   * @param duration - The duration of the contract (default is contractDuration)
   * @param tariffIncrease - The yearly tariff increase (default is yearlyTariffIncrease)
   * @param escClause - The escalation clause (default is escalationClause)
   * @returns An array of margin data for each year
   */
  const calculateMarginErosion = (
    price: number,
    duration: number = contractDuration,
    tariffIncrease: number = yearlyTariffIncrease,
    escClause: number = escalationClause
  ) => {
    const initialPrice = price;
    const years = Array.from({length: duration + 1}, (_, i) => i);

    return years.map(year => {
      const adjustedWage = wage * Math.pow(1 + tariffIncrease, year);
      const adjustedPrice = initialPrice * Math.pow(1 + tariffIncrease * escClause, year);

      const margin = ((adjustedPrice - (adjustedWage + otherCosts)) / adjustedPrice) * 100;

      return {
        year: year.toString(),
        tenderMargin: Number(margin.toFixed(2)),
        comparisonMargin: Number(margin.toFixed(2)),
      };
    });
  };

  // Initialize margin erosion data on component mount
  useEffect(() => {
    if (contractDurationMonths > 12) {
      setMarginErosionData(calculateMarginErosion(price));
    } else {
      setMarginErosionData([]);
    }
    setCurrentDuration(contractDuration);
  }, [wage, price]);

  /**
   * Handles the submission of the comparison form
   * @param values - The form values
   */
  const onSubmit = (values: z.infer<typeof ComparisonSchema>) => {
    // If the duration has changed, recalculate the original data with the new duration
    if (values.contractDuration !== currentDuration) {
      setMarginErosionData(calculateMarginErosion(price, values.contractDuration));
      setCurrentDuration(values.contractDuration);
    }

    // Calculate the comparison price with the inputted operative margin
    const comparisonPrice = new Decimal(tenderCostDetails?.totalCosts ?? 0)
      .dividedBy(tenderHours)
      .dividedBy(new Decimal(1).minus(values.operativeMargin / 100))
      .toNumber();

    // Calculate comparison data with the form values
    const comparisonResult = calculateMarginErosion(
      comparisonPrice,
      values.contractDuration,
      values.yearlyTariffIncrease / 100,
      values.escalationClause / 100
    );
    setComparisonData(comparisonResult);
  };

  /**
   * Resets the form and comparison data
   */
  const handleReset = () => {
    form.reset();
    setComparisonData(null);

    // Reset the original data to use the initial contract duration
    if (currentDuration !== contractDuration) {
      setMarginErosionData(calculateMarginErosion(price));
      setCurrentDuration(contractDuration);
    }
  };

  /**
   * Calculates the domain for the Y-axis of the margin erosion chart
   * Takes into account both tender margin and comparison margin (if exists)
   * Adds 10% padding above and below the min/max values for better visualization
   * @returns Array containing [minDomain, maxDomain] for Y-axis
   */
  const calculateYAxisDomain = () => {
    const allValues = [...marginErosionData.map(data => data.tenderMargin)];
    if (comparisonData) {
      allValues.push(...comparisonData.map(data => data.comparisonMargin));
    }
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    // Add some padding to the domain
    const padding = (maxValue - minValue) * 0.1;
    return [Math.floor(minValue - padding), Math.ceil(maxValue + padding)];
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t("marginerosion.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Current chance values */}
        <div className="flex flex-col gap-2 mb-8">
          <div className="flex justify-start gap-10">
            <TextInfoGroupAlt
              label={t("marginerosion.currentchancevalues.escalationclause")}
              content={`${convertPercentageDecimalToDecimal(escalationClause).toFixed(2)}%`}
            />
            <TextInfoGroupAlt
              label={t("marginerosion.currentchancevalues.operativemargin")}
              content={`${convertPercentageDecimalToDecimal(operativeMargin).toFixed(2)}%`}
            />
            <TextInfoGroupAlt
              label={t("marginerosion.currentchancevalues.contractduration")}
              content={`${contractDuration > 1 ? `${contractDuration} Jahre` : `${contractDuration} Jahr`}`}
            />
            <TextInfoGroupAlt
              label={t("marginerosion.currentchancevalues.yearlytariffincrease")}
              content={`${convertPercentageDecimalToDecimal(yearlyTariffIncrease).toFixed(2)}%`}
            />
          </div>
          <MarginErosionText
            type="tender"
            contractDuration={currentDuration}
            marginErosionData={marginErosionData}
          />
          {contractDurationMonths <= 12 && (
            <p className="text-sm text-slate-400">{t("marginerosion.shortduration")}</p>
          )}
        </div>
        {/* Chart for margin erosion */}
        <ChartContainer config={chartConfig} className="h-[200px] w-full">
          <LineChart
            accessibilityLayer
            data={marginErosionData}
            margin={{
              top: 20,
              left: 12,
              right: 20,
            }}
            width={800}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="year"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.slice(0, 3)}
              label={{ value: t("marginerosion.graph.year"), position: "insideBottom", offset: -5 }}
            />
            <YAxis
              dataKey="tenderMargin"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => `${value}%`}
              domain={calculateYAxisDomain()}
              allowDataOverflow={true}
              label={{ value: t("marginerosion.graph.operativemargin"), angle: -90, position: "insideBottomLeft", offset: 0 }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  indicator="line"
                  className="bg-white"
                  labelFormatter={(label) => {
                    return `${t("marginerosion.graph.year")}: ${label}`;
                  }}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            <ReferenceLine y={0} stroke="red" />
            {comparisonData && (
              <Line
                name={t("marginerosion.graph.comparisonmargin")}
                data={comparisonData}
                dataKey="comparisonMargin"
                type="natural"
                stroke={chartConfig.comparisonMargin.color}
                strokeWidth={2}
                dot={{
                  fill: chartConfig.comparisonMargin.color,
                }}
                activeDot={{
                  r: 6,
                }}
              >
                <LabelList
                  position="bottom"
                  offset={12}
                  className="fill-foreground"
                  fontSize={12}
                />
              </Line>
            )}
            <Line
              name={t("marginerosion.graph.tendermargin")}
              dataKey="tenderMargin"
              type="natural"
              stroke={chartConfig.tenderMargin.color}
              strokeWidth={2}
              dot={{
                fill: chartConfig.tenderMargin.color,
              }}
              activeDot={{
                r: 6,
              }}
            >
              <LabelList
                position="top"
                offset={12}
                className="fill-foreground"
                fontSize={12}
              />
            </Line>
          </LineChart>
        </ChartContainer>
        {/* Accordion for comparison form */}
        <Accordion type="single" collapsible defaultValue="compare">
          <AccordionItem value="compare">
            <AccordionTrigger>{t("marginerosion.compare.title")}</AccordionTrigger>
            <AccordionContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
                  {comparisonData && (
                    <div className="mb-4">
                      <MarginErosionText
                        type="comparison"
                        contractDuration={form.getValues().contractDuration}
                        marginErosionData={comparisonData}
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap lg:flex-nowrap gap-2 px-1 items-end">
                    {
                      comparisonInputFields.map((field) => (
                        <FormattedNumberInputField
                          key={field.name}
                          control={form.control}
                          isStandalone={true}
                          name={field.name}
                          label={field.label}
                          placeholder={field.placeholder}
                          isInteger={field.isInteger}
                          style={field.style as "currency" | "decimal"}
                          additionalSuffix={field.additionalSuffix}
                        />
                      ))
                    }
                    <div className="flex justify-end gap-2 mt-5.5">
                      <Button type="button" variant="outline" onClick={() => handleReset()}>{t("common:buttons.reset")}</Button>
                      <Button type="submit">{t("common:buttons.compare")}</Button>
                    </div>
                  </div>
                </form>
              </Form>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}

export default MarginErosionVisualiserComponent;
